import { useState } from 'react';
import { Lock, Eye, EyeOff, Mail, CheckCircle2, Building2 } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useMatter } from '../hooks/useMatter';
import { ADI_AGENT } from '../schema/adi-agent';

const formatPhone = (p: string) => `+${p.slice(0, 2)} ${p.slice(2)}`;
const formatAddress = (a: { line1: string; suburb: string; state: string; postcode: string }) =>
  `${a.line1}, ${a.suburb} ${a.state} ${a.postcode}`;

const MIN_PASSWORD_LENGTH = 8;

export default function AccountSettings() {
  const { user, setPassword: doSetPassword } = useAuth();

  const [password, setPasswordValue] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setError('Passwords don’t match.');
      return;
    }

    setLoading(true);
    try {
      await doSetPassword(password);
      setSuccess(true);
      setPasswordValue('');
      setConfirm('');
    } catch (err) {
      setError(humaniseError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-xl">
      <h1 className="text-2xl font-semibold text-[#0B2341] mb-1">Account settings</h1>
      <p className="text-slate-500 text-sm mb-6">Manage your sign-in details.</p>

      {user?.email && (
        <div className="card p-6 mb-6">
          <label className="form-label">Email</label>
          <div className="relative">
            <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="email"
              value={user.email}
              readOnly
              disabled
              className="form-input pl-10 bg-slate-50 text-slate-500 cursor-not-allowed"
            />
          </div>
          <p className="text-xs text-slate-400 mt-2">
            To change your email, contact us on 1300 729 331.
          </p>
        </div>
      )}

      <div className="card p-6">
        <h2 className="text-lg font-semibold text-[#0B2341] mb-4">Change password</h2>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="form-label">New password</label>
            <div className="relative">
              <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type={show ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPasswordValue(e.target.value)}
                placeholder="At least 8 characters"
                className="form-input pl-10 pr-10"
                required
                autoComplete="new-password"
                minLength={MIN_PASSWORD_LENGTH}
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                aria-label={show ? 'Hide password' : 'Show password'}
              >
                {show ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div>
            <label className="form-label">Confirm new password</label>
            <div className="relative">
              <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type={show ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Re-enter password"
                className="form-input pl-10"
                required
                autoComplete="new-password"
                minLength={MIN_PASSWORD_LENGTH}
              />
            </div>
          </div>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          {success && (
            <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700 flex items-center gap-2">
              <CheckCircle2 size={16} />
              Your password has been updated.
            </div>
          )}

          <button type="submit" disabled={loading} className="btn-primary justify-center">
            {loading ? 'Saving...' : 'Update password'}
          </button>
        </form>
      </div>

      <AgentDefaultsCard />
    </div>
  );
}

/**
 * Staff-only reference card: the ADI agent-of-record defaults seeded into
 * every new intake form (authorised recipient, postal address, contact
 * channels). Read-only on purpose - the values are compliance-verified
 * against the OMARA register and managed centrally; a change applies to new
 * forms on deploy and to existing forms via the backfill.
 */
function AgentDefaultsCard() {
  const { actuallyStaff } = useMatter();
  if (!actuallyStaff) return null;
  const rows: Array<[string, string]> = [
    ['Registered migration agent', `${ADI_AGENT.title} ${ADI_AGENT.givenNames} ${ADI_AGENT.familyName}`],
    ['MARN', ADI_AGENT.marn],
    ['Legal entity', `${ADI_AGENT.legalEntity} · ABN ${ADI_AGENT.abn}`],
    ['Organisation', ADI_AGENT.organisation],
    ['Email (correspondence)', ADI_AGENT.email],
    ['Business phone', formatPhone(ADI_AGENT.phoneBusiness)],
    ['Mobile (also home / after hours)', formatPhone(ADI_AGENT.phoneMobile)],
    ['Postal address (all Departmental mail)', formatAddress(ADI_AGENT.postalAddress)],
    ['Business address (OMARA-registered)', formatAddress(ADI_AGENT.address)],
  ];
  return (
    <div className="card p-6 mt-6">
      <div className="flex items-center gap-2 mb-1">
        <Building2 size={16} className="text-[#2CABEA]" />
        <h2 className="text-base font-semibold text-[#0B2341]">Agent of record defaults</h2>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Seeded into every new intake form as the authorised recipient and correspondence details.
        Verified against the OMARA register and managed centrally: to change a value, ask Tzvi -
        the update applies to new forms on the next release, and to existing forms via a backfill.
      </p>
      <dl className="space-y-2">
        {rows.map(([label, value]) => (
          <div key={label} className="flex flex-col sm:flex-row sm:items-baseline sm:gap-3">
            <dt className="text-xs font-medium uppercase tracking-wide text-slate-400 sm:w-64 shrink-0">{label}</dt>
            <dd className="text-sm text-slate-700">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function humaniseError(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err ?? 'Something went wrong.');
  const lower = raw.toLowerCase();
  if (lower.includes('rate') || lower.includes('too many')) {
    return 'Too many attempts. Please wait a minute and try again.';
  }
  if (lower.includes('same') && lower.includes('password')) {
    return 'Your new password must be different from your current one.';
  }
  return raw;
}
